
import {
  getPersonalSettings,
  updatePersonalSettings,
} from "../api/settings.js";
import { settings } from "../stores/settings.js";

const styles = css`
  form {
    display: grid;
    gap: 20px;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    font-weight: 500;
    gap: 6px;
  }

  input,
  textarea {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    outline: none;
    padding: 8px 10px;
    transition:
      border-color 140ms ease,
      box-shadow 140ms ease;
    width: 100%;
  }

  input {
    height: 36px;
  }

  textarea {
    line-height: 1.5;
    min-height: 180px;
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  input:disabled {
    background: var(--muted);
    color: var(--muted-foreground);
    cursor: not-allowed;
  }

  .hint,
  .status {
    color: var(--muted-foreground);
    font-size: 12px;
    font-weight: 400;
    line-height: 1.45;
  }

  .actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .actions app-button {
    width: auto;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
  }

  .error:empty {
    display: none;
  }
`;

export class AppSettingsPersonal extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsPersonal)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #username;
  #fullName;
  #personality;
  #loaded;
  #saving;
  #saved;
  #error;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #attr0;
  #attr1;
  #attr2;
  #attr3;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #personalityArea = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="personalityArea"]') : null; } }; })();
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setUsername(value) {
    if (Object.is(this.#username, value)) return;
    this.#username = value;
    this.#updateBindings('username');
  }

  #setFullName(value) {
    if (Object.is(this.#fullName, value)) return;
    this.#fullName = value;
    this.#updateBindings('fullName');
  }

  #setPersonality(value) {
    if (Object.is(this.#personality, value)) return;
    this.#personality = value;
    this.#updateBindings('personality');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setSaving(value) {
    if (Object.is(this.#saving, value)) return;
    this.#saving = value;
    this.#updateBindings('saving');
  }

  #setSaved(value) {
    if (Object.is(this.#saved, value)) return;
    this.#saved = value;
    this.#updateBindings('saved');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void getPersonalSettings()
        .then((profile) => {
        this.#setUsername(profile.username);
        this.#setFullName(profile.full_name);
        this.#setPersonality(profile.personality);
        settings.username = profile.username;
        settings.fullName = profile.full_name;
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to load your personal settings.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }


  #initState() {
    this.#username = "";
    this.#fullName = "";
    this.#personality = "";
    this.#loaded = false;
    this.#saving = false;
    this.#saved = false;
    this.#error = "";
  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Profile" description="Choose how your name appears in Stride. Your username remains fixed and is used to sign in."><form data-argon="2631"><label>Full name<input name="full-name" autocomplete="name" value="${this.#escAttr(this.#fullName)}"${((!this.#loaded) || this.#saving) ? ' disabled' : ''} data-argon="3886" data-argon-bind="0"></label><label>Username<input name="username" value="${this.#escAttr(this.#username)}" disabled data-argon-bind="1"><span class="hint">Your username is set when the account is created.</span></label><label>Personality<textarea data-argon-ref="personalityArea" name="personality" placeholder="Tell Stride how you prefer to work, communicate, and make decisions."${((!this.#loaded) || this.#saving) ? ' disabled' : ''} data-argon="4563" data-argon-bind="2"></textarea><span class="hint">Stride includes this context when working with you. Leave it blank to use the default behavior.</span></label><div class="actions"><app-button type="submit"${((!this.#loaded) || this.#saving) ? ' disabled' : ''} data-argon-bind="3"><!--argon-text:0-->${this.#escText(this.#saving ? "Saving..." : "Save changes")}<!--/argon-text:0--></app-button><!--argon-text:1-->${this.#saved ? "<span class=\"status\">Saved.</span>" : this.#escText("")}${((!this.#loaded) && (!this.#error)) ? "<span class=\"status\">Loading...</span>" : this.#escText("")}<!--/argon-text:1--><span class="error"><!--argon-text:2-->${this.#escText(this.#error)}<!--/argon-text:2--></span></div></form></app-settings-section>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#attr3 = root.querySelector('[data-argon-bind="3"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const area = this.#personalityArea.current;
    if (area && area.value !== this.#personality)
        area.value = this.#personality;
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'saving') {
      const __argonNext = `${this.#escText(this.#saving ? "Saving..." : "Save changes")}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'saved' || prop === 'loaded' || prop === 'error') {
      const __argonNext = `${this.#saved ? "<span class=\"status\">Saved.</span>" : this.#escText("")}${((!this.#loaded) && (!this.#error)) ? "<span class=\"status\">Loading...</span>" : this.#escText("")}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'fullName') && this.#attr0) {
      const __argonNext = `${this.#fullName}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('value', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'loaded' || prop === 'saving') && this.#attr0) {
      this.#attr0.toggleAttribute('disabled', !!((!this.#loaded) || this.#saving));
    }
    if ((prop === undefined || prop === 'username') && this.#attr1) {
      const __argonNext = `${this.#username}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr1.setAttribute('value', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'loaded' || prop === 'saving') && this.#attr2) {
      this.#attr2.toggleAttribute('disabled', !!((!this.#loaded) || this.#saving));
    }
    if ((prop === undefined || prop === 'loaded' || prop === 'saving') && this.#attr3) {
      this.#attr3.toggleAttribute('disabled', !!((!this.#loaded) || this.#saving));
    }
    if (this.#hooksLive && (prop === 'personality')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2631').forEach(el => this.#listen(el, '2631', 'submit', (event: Event) => {
    event.preventDefault();
    const nextName = this.#fullName.trim();
    if (!nextName) {
        this.#setError("Full name cannot be empty.");
        return;
    }
    this.#setSaving(true);
    this.#setSaved(false);
    this.#setError("");
    void updatePersonalSettings({
        full_name: nextName,
        personality: this.#personality.trim(),
    })
        .then((profile) => {
        this.#setUsername(profile.username);
        this.#setFullName(profile.full_name);
        this.#setPersonality(profile.personality);
        settings.username = profile.username;
        settings.fullName = profile.full_name;
        this.#setSaved(true);
    })
        .catch((reason) => {
        this.#setError(reason instanceof Error ? reason.message : "Failed to save your personal settings.");
    })
        .finally(() => {
        this.#setSaving(false);
    });
}));
    this.#eventTargets(scope, '3886').forEach(el => this.#listen(el, '3886', 'input', (event: Event) => {
    this.#setFullName((event.target as HTMLInputElement).value);
    this.#setSaved(false);
}));
    this.#eventTargets(scope, '4563').forEach(el => this.#listen(el, '4563', 'input', (event: Event) => {
    this.#setPersonality((event.target as HTMLTextAreaElement).value);
    this.#setSaved(false);
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
